import { useCallback, useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import usePermissions from '../../../../../hooks/usePermissions'

import { insertIf } from '../../../../../helpers/common'
import { redirectTo } from '../../../../../helpers/url'
import { createCampaignSummaryPageLink } from '../../../../Campaigns/helpers'

import { clearUpdateCampaign, updateCampaign } from '../../../../../modules/actions/campaigns'
import { UPDATE_COMBINED_CAMPAIGN } from '../../../../../modules/actions/combinedData'
import { selectedAdAccountsSelector } from '../../../../../modules/selectors/adAccounts'
import { campaignUpdateErrorSelector, campaignWasUpdatedSelector } from '../../../../../modules/selectors/campaigns'
import { openForm } from '../../../../../modules/actions/forms'

import { listItemChangeStatus } from '../../../../../constants/other'
import { PROVIDER_TO_PLATFORMS } from '../../../../../constants/selectLists/platformList'
import { HISTORY_META_EDIT_SECTION } from '../../../../../constants/changeHistory'
import { COMBINED_CAMPAIGN_EDIT } from '../../../../../constants/forms'
import { combinedCampaignsSelector } from '../../../../../modules/selectors/combinedData'

const useGetRightCampaignActions = handleDeleteRequest => {
  const dispatch = useDispatch()

  const permissions = usePermissions()

  const selectedAdAccounts = useSelector(selectedAdAccountsSelector)
  const campaignUpdateWasUpdated = useSelector(campaignWasUpdatedSelector)
  const campaignUpdateError = useSelector(campaignUpdateErrorSelector)
  const { campaigns = [] } = useSelector(combinedCampaignsSelector)

  const someCombinedCampaignsHasError = useMemo(() => campaigns.some(campaign => campaign.error), [campaigns])

  const redirectToCampaignsPage = useCallback(campaign => {
    redirectTo(createCampaignSummaryPageLink(campaign))
  }, [])

  useEffect(() => {
    if (campaignUpdateWasUpdated || campaignUpdateError) {
      dispatch(clearUpdateCampaign())
    }
  }, [dispatch, campaignUpdateWasUpdated, campaignUpdateError])

  return useCallback(
    campaign => {
      const { id, provider, status } = campaign

      return [
        {
          text: 'Go to campaign',
          onClickHandler: () => redirectToCampaignsPage(campaign)
        },
        ...insertIf(permissions.can('update', 'Campaign'), {
          text: status === 'active' ? 'Pause' : 'Activate',
          onClickHandler: () => {
            dispatch(
              updateCampaign(
                {
                  status: listItemChangeStatus[status].changeTo,
                  account: selectedAdAccounts[PROVIDER_TO_PLATFORMS[provider]]?.id,
                  meta: {
                    [HISTORY_META_EDIT_SECTION]: 'Status'
                  }
                },
                id,
                PROVIDER_TO_PLATFORMS[provider],
                {
                  updateType: UPDATE_COMBINED_CAMPAIGN
                }
              )
            )
          }
        }),
        ...insertIf(!someCombinedCampaignsHasError && permissions?.can('manage', COMBINED_CAMPAIGN_EDIT), {
          text: 'Edit',
          onClickHandler: () => {
            dispatch(
              openForm({
                id,
                formName: COMBINED_CAMPAIGN_EDIT
              })
            )
          }
        }),
        ...insertIf(
          // for now, we show delete action for facebook and tiktok only
          (campaign.provider === 'facebook' || campaign.provider === 'tiktok') && permissions.can('delete', 'Campaign'),
          {
            text: 'Delete',
            onClickHandler: () => {
              handleDeleteRequest({
                item: campaign,
                platform: PROVIDER_TO_PLATFORMS[provider],
                account: selectedAdAccounts[PROVIDER_TO_PLATFORMS[provider]]?.id
              })
            }
          }
        )
      ]
    },
    [
      dispatch,
      permissions,
      redirectToCampaignsPage,
      selectedAdAccounts,
      someCombinedCampaignsHasError,
      handleDeleteRequest
    ]
  )
}

export default useGetRightCampaignActions
